/**
 * Store domain entity representing a physical or virtual store
 *
 * Encapsulates store logic with validation and business rules.
 * Stores are fundamental entities for organizing personnel and resources.
 * Immutable by design to ensure data integrity.
 *
 * @module domains/personnel/domain/entities/Store
 */

import type { StoreId } from '@personnel/domain/types/EmployeeTypeSafe';

/**
 * Store entity representing a physical or virtual location.
 *
 * Immutable — only created/reconstructed via the factory function.
 *
 * @example
 * const store = createStoreFromData({ uuid: 'store-uuid-123', name: 'Sucursal Centro', isDraft: false });
 */
export type Store = Readonly<{
  uuid: StoreId;
  name: string;
  isDraft: boolean;
}>;

/**
 * Reconstructs a Store entity from persisted/raw data.
 *
 * @param params - Raw primitives (typically from the API mapper)
 * @returns Frozen Store entity
 * @throws Error if uuid or name is empty
 */
export const createStoreFromData = (params: { uuid: string; name: string; isDraft?: boolean }): Store => {
  if (!params.uuid.trim()) {
    throw new Error('Store uuid es obligatorio');
  }
  if (!params.name.trim()) {
    throw new Error('Store name es obligatorio');
  }
  return Object.freeze({
    uuid: params.uuid as StoreId,
    name: params.name,
    isDraft: params.isDraft ?? false,
  });
};
